import React from "react";
import { Container, Typography, Box, Divider } from "@mui/material";
import { useLanguage } from "../../contexts/LanguageContext";

interface Section {
  heading: string;
  content: string | string[];
}

const AgbPage: React.FC = () => {
  const { currentLanguage } = useLanguage();

  const isAr = currentLanguage === "ar";

  const sections: Section[] =
    currentLanguage === "ar"
      ? [
          {
            heading: "§ 1 نطاق التطبيق",
            content:
              "تسري هذه الشروط والأحكام العامة على جميع العقود المبرمة بين TOP BHS والعملاء فيما يخص الخدمات والتطوير البرمجي واستشارات تكنولوجيا المعلومات. تسري شروط المورد المتعارضة فقط إذا وافقنا عليها صراحةً.",
          },
          {
            heading: "§ 2 إبرام العقد",
            content: [
              "تُعدّ عروضنا غير ملزمة وغير مُلزِمة.",
              "يصدر عرض ملزم من العميل عند إرسال طلب الخدمة.",
              "يتم إبرام العقد بقبولنا الكتابي للطلب أو بالبدء في تنفيذ الخدمة.",
            ],
          },
          {
            heading: "§ 3 الخدمات والتعديلات",
            content:
              "نُحدد نطاق الخدمة في مواصفات المشروع المتفق عليها. يجب الاتفاق على التغييرات التي تطرأ بعد إبرام العقد كتابياً وقد تؤدي إلى تعديل التكاليف والجداول الزمنية.",
          },
          {
            heading: "§ 4 الأسعار والدفع",
            content: [
              "الأسعار المتفق عليها صافية زائد ضريبة القيمة المضافة القانونية.",
              "يستحق الدفع خلال 14 يوماً من تاريخ الفاتورة ما لم يُتفق على خلاف ذلك.",
              "في حالة التأخر في السداد، نحتفظ بالحق في المطالبة بفائدة التأخير وفق الأحكام القانونية.",
            ],
          },
          {
            heading: "§ 5 حقوق النشر والملكية الفكرية",
            content:
              "تبقى جميع حقوق النشر الخاصة بالبرمجيات والتصاميم والمواد الإبداعية المطورة ملكاً لـ TOP BHS حتى سداد المبلغ الكامل. بعد السداد الكامل تنتقل حقوق الاستخدام المتفق عليها إلى العميل.",
          },
          {
            heading: "§ 6 حدود المسؤولية",
            content:
              "نتحمل المسؤولية كاملةً في حالات الإهمال الجسيم والتعمد والإخلال بالالتزامات التعاقدية الجوهرية. في غير ذلك تُحدد مسؤوليتنا بالأضرار المتوقعة المعتادة وقت إبرام العقد.",
          },
          {
            heading: "§ 7 السرية",
            content:
              "يتعهد الطرفان بالحفاظ على سرية المعلومات السرية التي يتلقاها كل طرف من الآخر وعدم إفشائها لأطراف ثالثة.",
          },
          {
            heading: "§ 8 القانون الواجب التطبيق والاختصاص القضائي",
            content:
              "يسري قانون جمهورية ألمانيا الاتحادية. يكون مكان الاختصاص القضائي مقر TOP BHS في حال كان العميل تاجراً.",
          },
        ]
      : currentLanguage === "en"
        ? [
            {
              heading: "§ 1 Scope",
              content:
                "These General Terms and Conditions apply to all contracts between TOP BHS and clients regarding services, software development, and IT consulting. Conflicting terms of the client apply only if we expressly agree to them.",
            },
            {
              heading: "§ 2 Conclusion of Contract",
              content: [
                "Our offers are non-binding and subject to change.",
                "A binding offer from the client is made when the service request is submitted.",
                "The contract is concluded upon our written acceptance or the commencement of service delivery.",
              ],
            },
            {
              heading: "§ 3 Services and Changes",
              content:
                "The scope of services is defined in the agreed project specification. Changes after contract conclusion must be agreed in writing and may result in adjusted costs and timelines.",
            },
            {
              heading: "§ 4 Prices and Payment",
              content: [
                "Agreed prices are net plus applicable VAT.",
                "Payment is due within 14 days of invoicing unless otherwise agreed.",
                "In case of late payment, we reserve the right to charge statutory default interest.",
              ],
            },
            {
              heading: "§ 5 Copyright and Intellectual Property",
              content:
                "All copyrights to developed software, designs, and creative materials remain with TOP BHS until full payment. After full payment, the agreed usage rights are transferred to the client.",
            },
            {
              heading: "§ 6 Limitation of Liability",
              content:
                "We accept full liability in cases of gross negligence, intent, and breach of material contractual obligations. In all other cases, our liability is limited to foreseeable damage typical at the time of contracting.",
            },
            {
              heading: "§ 7 Confidentiality",
              content:
                "Both parties undertake to maintain confidentiality regarding confidential information received from the other party and not to disclose it to third parties.",
            },
            {
              heading: "§ 8 Governing Law and Jurisdiction",
              content:
                "The law of the Federal Republic of Germany applies. The place of jurisdiction is the registered office of TOP BHS, provided the client is a merchant.",
            },
          ]
        : [
            {
              heading: "§ 1 Geltungsbereich",
              content:
                "Diese Allgemeinen Geschäftsbedingungen gelten für alle Verträge zwischen TOP BHS und Kunden über Dienstleistungen, Softwareentwicklung und IT-Beratung. Abweichende Bedingungen des Kunden gelten nur, wenn wir diesen ausdrücklich zugestimmt haben.",
            },
            {
              heading: "§ 2 Vertragsschluss",
              content: [
                "Unsere Angebote sind freibleibend und unverbindlich.",
                "Ein verbindliches Angebot des Kunden liegt mit Absenden der Serviceanfrage vor.",
                "Der Vertrag kommt durch unsere schriftliche Auftragsbestätigung oder mit Beginn der Leistungserbringung zustande.",
              ],
            },
            {
              heading: "§ 3 Leistungen und Änderungen",
              content:
                "Der Leistungsumfang ergibt sich aus der vereinbarten Projektspezifikation. Änderungen nach Vertragsschluss bedürfen der Schriftform und können zu angepassten Kosten und Zeitplänen führen.",
            },
            {
              heading: "§ 4 Preise und Zahlung",
              content: [
                "Vereinbarte Preise verstehen sich netto zuzüglich der gesetzlichen Umsatzsteuer.",
                "Rechnungen sind innerhalb von 14 Tagen nach Rechnungsstellung fällig, sofern nicht anders vereinbart.",
                "Bei Zahlungsverzug behalten wir uns vor, gesetzliche Verzugszinsen zu berechnen.",
              ],
            },
            {
              heading: "§ 5 Urheberrecht und geistiges Eigentum",
              content:
                "Alle Urheberrechte an entwickelter Software, Designs und kreativen Materialien verbleiben bis zur vollständigen Bezahlung bei der TOP BHS. Nach vollständiger Zahlung werden die vereinbarten Nutzungsrechte auf den Kunden übertragen.",
            },
            {
              heading: "§ 6 Haftungsbeschränkung",
              content:
                "Wir haften in vollem Umfang bei grober Fahrlässigkeit, Vorsatz und Verletzung wesentlicher Vertragspflichten. Im Übrigen ist unsere Haftung auf den vorhersehbaren, vertragstypischen Schaden bei Vertragsschluss begrenzt.",
            },
            {
              heading: "§ 7 Vertraulichkeit",
              content:
                "Beide Parteien verpflichten sich, vertrauliche Informationen, die sie von der jeweils anderen Partei erhalten, vertraulich zu behandeln und nicht an Dritte weiterzugeben.",
            },
            {
              heading: "§ 8 Anwendbares Recht und Gerichtsstand",
              content:
                "Es gilt das Recht der Bundesrepublik Deutschland. Gerichtsstand ist der Sitz der TOP BHS, sofern der Kunde Kaufmann ist.",
            },
          ];

  const title =
    currentLanguage === "ar"
      ? "الشروط والأحكام العامة"
      : currentLanguage === "en"
        ? "Terms and Conditions"
        : "Allgemeine Geschäftsbedingungen";

  const overline =
    currentLanguage === "ar"
      ? "قانوني"
      : currentLanguage === "en"
        ? "Legal"
        : "Rechtliches";

  return (
    <Container maxWidth="md" sx={{ py: 10, dir: isAr ? "rtl" : "ltr" }}>
      <Typography
        variant="overline"
        sx={{
          color: "text.secondary",
          letterSpacing: 3,
          display: "block",
          mb: 2,
        }}
      >
        {overline}
      </Typography>
      <Typography variant="h2" sx={{ mb: 6 }}>
        {title}
      </Typography>

      {sections.map((section, idx) => (
        <Box key={idx} sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            {section.heading}
          </Typography>
          {Array.isArray(section.content) ? (
            section.content.map((line, i) => (
              <Typography key={i} variant="body1" sx={{ mb: 0.5 }}>
                {line}
              </Typography>
            ))
          ) : (
            <Typography variant="body1">{section.content}</Typography>
          )}
          {idx < sections.length - 1 && <Divider sx={{ mt: 4 }} />}
        </Box>
      ))}
    </Container>
  );
};

export default AgbPage;
